import * as THREE from 'three';
import { FACTION_COLOR, type ClassId, type Faction } from '../../core/constants.ts';

/**
 * What the renderer asks for, and what it never asks for.
 *
 * Call sites name a *role* — `mage.cleric`, `prop.torch` — and never a file, a
 * pack or a mesh. Everything that differs between one art library and another
 * lives in the manifest instead: paths, scales, axis conventions, clip names.
 * Swapping packs is then editing data rather than touching the renderer.
 *
 * Every role has a procedural fallback, which is what the game has been drawing
 * all along. That is the part that makes the art decision reversible: a role
 * with no asset behind it renders as its gray-box primitive, so the game runs
 * with no models at all, with some, or with a different set entirely.
 *
 * Note what is deliberately *not* here. Floors, walls and ceilings are generated
 * from arbitrary box dimensions, and stretching a mesh to fit an arbitrary box
 * looks like stretched geometry. Replacing those wants a modular kit or authored
 * rooms — a real decision, not something to pre-empt with a scale factor.
 * Pillars are in because they are discrete objects placed one at a time.
 */
export const ASSET_ROLES = [
  'mage.magician',
  'mage.cleric',
  'mage.mentalist',
  'mage.arcanist',
  'prop.torch',
  'prop.shrine',
  'prop.pool',
  'level.pillar',
  // Scenery that blocks. The box in `LevelGeometry` stays authoritative for
  // collision and sight; these are only what it looks like.
  'level.bookshelf',
  'level.crate',
  // The cathedral's furniture. Scenery like the two above: a box in
  // `LevelGeometry` still decides what blocks and what can be seen through, and
  // these are only what that box looks like.
  'prop.bench',
  'prop.cauldron',
  'prop.workbench',
  'prop.crateMetal',
  'prop.crateFarm',
  'prop.arch',
  'prop.cage',
  'prop.roofSupport',
  'prop.chain',
  // The viewmodel: a gloved hand holding a wand, one per discipline. This is the
  // only thing a player sees of their own class for the whole match, which makes
  // four distinct wands four class identities for almost nothing.
  'viewmodel.magician',
  'viewmodel.cleric',
  'viewmodel.mentalist',
  'viewmodel.arcanist',
  /*
   * Projectile shapes.
   *
   * A role per shape rather than per spell, because the grammar is already
   * per shape: every spell that throws a `mace` throws the same object, and
   * Wounding and a Cleric's future variants should not need a model each.
   */
  'projectile.mace',
  'projectile.blade',
  'projectile.shard',
  'projectile.stone',
  'projectile.mass',
  'projectile.barb',
  'projectile.orb',
] as const;

export type AssetRole = (typeof ASSET_ROLES)[number];

export const mageRole = (cls: ClassId): AssetRole => `mage.${cls}` as AssetRole;
export const wandRole = (cls: ClassId): AssetRole => `viewmodel.${cls}` as AssetRole;
export const projectileRole = (shape: string): AssetRole => `projectile.${shape}` as AssetRole;

/**
 * Where a wand's tip sits **relative to the grip**, not to the camera.
 *
 * Everything about a viewmodel is built around the grip and then held out at
 * arm's length by whatever is holding it. Building it in camera space instead is
 * how the first version ended up with a hand wrapped around the lens.
 */
export const WAND_TIP = { x: 0, y: 0.02, z: -0.44 };

/**
 * Logical animation states. A pack's own clip names are mapped onto these in the
 * manifest, because every library names them differently and the renderer should
 * never learn which one it is talking to.
 */
export type AnimState =
  | 'idle' | 'walk' | 'jog' | 'run'
  | 'crouchIdle' | 'crouchWalk'
  | 'jump'
  // The cast is four clips, not one, because the game's wind-up is three
  // distinct moments: raising, holding, and letting go.
  | 'castStart' | 'castHold' | 'castRelease' | 'castEnd'
  | 'hit' | 'die';

export interface CreateRequest {
  role: AssetRole;
  /** Target extents, for roles placed into a box. */
  size?: { x: number; y: number; z: number };
  /** Tint, for anything that carries faction or element colour. */
  color?: number;
  /** Which side this one is on, for models with a wardrobe per faction. */
  faction?: Faction;
}

/**
 * Everything the registry hands back is anchored at the centre of its base, so
 * a call site places it at floor height and stops caring whether it is a loaded
 * mesh or a capsule. Models are authored feet-at-origin far more often than not,
 * and the fallbacks are shifted to match rather than the other way round.
 */
export type Fallback = (req: CreateRequest) => THREE.Object3D;

/** Wraps a mesh so the group's origin sits at the base of the geometry. */
function standing(mesh: THREE.Object3D, height: number): THREE.Group {
  const group = new THREE.Group();
  mesh.position.y = height / 2;
  group.add(mesh);
  return group;
}

const MAGE_HEIGHT = 1.8;

function mageFallback(req: CreateRequest): THREE.Object3D {
  const colour = req.color ?? FACTION_COLOR.order;
  const mesh = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.4, 1.0, 6, 10),
    new THREE.MeshStandardMaterial({
      color: colour, roughness: 0.6, emissive: colour, emissiveIntensity: 0.25,
    }),
  );
  return standing(mesh, MAGE_HEIGHT);
}

/*
 * The hand, measured off the Quaternius rig rather than guessed.
 *
 * The mage models carry a full finger hierarchy — `index_01_r` through
 * `pinky_04_leaf_r` — so the proportions of the hand the player sees can be the
 * proportions of the hands everybody else in the match has. Taken from the rest
 * pose in the hand's own frame, in metres:
 *
 *     palm, wrist to knuckles   0.110
 *     knuckle spread, index..pinky  0.075
 *     index / middle / ring / pinky  0.085 / 0.098 / 0.093 / 0.081
 *     thumb                     0.113, springing a quarter of the way up
 *     segments, per finger      38% / 31% / 31%
 *
 * Scaled to `HAND_SCALE` because a viewmodel is not a character: it is held
 * thirty centimetres from the eye and a hand at true size fills a third of the
 * screen. Everything else is a ratio, so the whole hand resizes from one number.
 */
const HAND_SCALE = 0.82;

/** Fingers, in the order they lie along the shaft: index nearest the tip. */
const FINGERS = [
  { name: 'index', length: 0.085, along: -0.030, spread: 0.02 },
  { name: 'middle', length: 0.098, along: -0.008, spread: 0 },
  { name: 'ring', length: 0.093, along: 0.014, spread: -0.015 },
  { name: 'pinky', length: 0.081, along: 0.034, spread: -0.03 },
];

/** Proportions of a finger's three bones, tip last. */
const SEGMENTS = [0.38, 0.31, 0.31];
/**
 * How far each joint bends, radians.
 *
 * A closed grip, not a fist: the total is a little over a half turn, which wraps
 * the shaft without driving the tips through the palm. Knuckle bends most,
 * because it does.
 */
const CURL = [1.05, 0.95, 0.72];
/** Radius the fingers wrap at — the shaft plus the thickness of a finger. */
const GRIP_RADIUS = 0.017;

/**
 * One finger: three tapering bones, each hinged off the end of the last.
 *
 * Built as nested groups rather than as placed meshes, so the curl is expressed
 * once per joint and the arithmetic of where a fingertip ends up is the scene
 * graph's problem.
 *
 * Segments point along +Y in their own frame and bend about **Z**, which keeps a
 * finger in the plane perpendicular to the shaft — the plane it has to stay in to
 * wrap round it. Bending about X instead, which is the obvious guess when the
 * knuckle line runs along Z, curls the finger *sideways along* the shaft: the
 * first version did exactly that and the tips came out 5–8cm behind the hand
 * instead of underneath it. Only a probe of where the tips landed caught it,
 * because the model still looked like a hand-shaped thing from the numbers up.
 */
function digit(
  length: number, radius: number, curls: readonly number[], material: THREE.Material,
): THREE.Group {
  const root = new THREE.Group();
  let joint = root;

  SEGMENTS.forEach((share, i) => {
    const bone = length * share;
    // Fingers taper toward the tip, which is most of what stops a hand reading
    // as four sausages.
    const near = radius * (1 - i * 0.13);
    const far = radius * (1 - (i + 1) * 0.13);

    joint.rotation.z = curls[i] ?? 0;
    const mesh = new THREE.Mesh(
      new THREE.CylinderGeometry(far, near, bone, 6),
      material,
    );
    mesh.position.y = bone / 2;
    joint.add(mesh);

    // A knuckle, so the seam between bones reads as a joint rather than a gap.
    const knuckle = new THREE.Mesh(new THREE.SphereGeometry(near * 1.02, 6, 4), material);
    joint.add(knuckle);

    const next = new THREE.Group();
    next.position.y = bone;
    joint.add(next);
    joint = next;
  });

  return root;
}

/**
 * A gloved hand wrapped round a wand, built to put the tip at WAND_TIP.
 *
 * One hand rather than two, and a fixed grip rather than an articulated one. A
 * hand that never opens never has to be animated, and it gives the spell one
 * obvious muzzle instead of the "which hand is casting" problem. What it does
 * now have is fingers: the first version was a cube, which read as a block of
 * wood at the bottom of the screen for the whole match.
 *
 * The shaft runs forward along −Z through the origin. Fingers lie side by side
 * *along* it — which is how a hand grips a rod, the knuckle line parallel to the
 * shaft — and curl around it from the back of the hand at −X, where the player
 * is looking from. The thumb crosses over the top.
 */
function viewmodel(build: (g: THREE.Group) => void): Fallback {
  return () => {
    const group = new THREE.Group();

    // Two tones, shared across every piece. One material per finger bone would
    // be eighteen materials for an object that is drawn once.
    const leather = new THREE.MeshStandardMaterial({ color: 0x2b2620, roughness: 0.9 });
    const worn = new THREE.MeshStandardMaterial({ color: 0x39322a, roughness: 0.85 });

    /*
     * The back of the hand: knuckles to wrist, sitting off the shaft on the side
     * the player sees. Slightly wedged — thicker at the knuckles than the
     * wrist — because a slab of even thickness is the thing that read as a cube.
     */
    const back = new THREE.Mesh(
      new THREE.BoxGeometry(0.030 * HAND_SCALE, 0.066 * HAND_SCALE, 0.100 * HAND_SCALE),
      leather,
    );
    back.position.set(-0.026 * HAND_SCALE, -0.004 * HAND_SCALE, 0.014 * HAND_SCALE);
    back.rotation.set(0.06, 0, -0.09);
    group.add(back);

    // The heel, filling the gap between the back of the hand and the fingertips
    // so the grip is not see-through from below.
    const heel = new THREE.Mesh(
      new THREE.BoxGeometry(0.040 * HAND_SCALE, 0.030 * HAND_SCALE, 0.086 * HAND_SCALE),
      worn,
    );
    heel.position.set(-0.006 * HAND_SCALE, -0.028 * HAND_SCALE, 0.012 * HAND_SCALE);
    heel.rotation.set(0.05, 0, -0.12);
    group.add(heel);

    for (const finger of FINGERS) {
      const hand = new THREE.Group();
      /*
       * Knuckles sit up and back from the shaft, and the fingers reach down and
       * round. `spread` fans them very slightly so the four are not a comb —
       * index highest, pinky trailing, the way a hand actually closes.
       */
      hand.position.set(
        -0.020 * HAND_SCALE,
        (0.012 + finger.spread * 0.25) * HAND_SCALE,
        finger.along * HAND_SCALE,
      );
      /*
       * Pointing down the far side of the shaft before the curl takes over.
       *
       * `spread` is a small turn about the shaft rather than about Z, so the four
       * fingers fan very slightly apart along the grip instead of stacking into
       * a comb — and the curl plane stays perpendicular to the shaft.
       */
      hand.rotation.set(finger.spread * 2.2, 0, Math.PI * 0.62);
      hand.add(digit(
        finger.length * HAND_SCALE,
        GRIP_RADIUS * HAND_SCALE * (finger.name === 'pinky' ? 0.84 : 1),
        CURL,
        finger.name === 'index' || finger.name === 'ring' ? leather : worn,
      ));
      group.add(hand);
    }

    /*
     * The thumb, which is what actually sells a hand.
     *
     * It springs from the front of the grip and lies *along* the shaft rather
     * than round it, pointing toward the tip — the way you hold a torch you are
     * pointing at something. Longer than any finger and only lightly bent.
     */
    const thumb = new THREE.Group();
    thumb.position.set(-0.019 * HAND_SCALE, 0.017 * HAND_SCALE, -0.020 * HAND_SCALE);
    /*
     * Turned to lie *along* the shaft rather than round it.
     *
     * The quarter turn about Y puts the digit's curl axis onto world X, so it
     * bends *down toward* the shaft as it reaches forward rather than swinging
     * sideways off the hand. The X term then swings its length onto −Z, pointing
     * it at the wand tip, with a quarter-radian of lift so it rides over the top
     * of the grip instead of through the fingers.
     *
     * The order matters and is not the obvious one. Three composes an 'XYZ'
     * Euler as Rx·Ry, so the Y term is applied *first* and only reorients the
     * plane the finger bends in — which is why setting it to angle the thumb
     * inward did nothing at all except move the tip further out.
     */
    thumb.rotation.set(-Math.PI * 0.5 + 0.30, Math.PI * 0.5, 0);
    // Negative: the thumb settles down onto the shaft as it reaches forward. The
    // same figures positive arch it up off the grip like a raised hitchhiker.
    thumb.add(digit(0.113 * HAND_SCALE, GRIP_RADIUS * HAND_SCALE * 1.12, [-0.30, -0.26, -0.20], leather));
    group.add(thumb);

    // The cuff of the robe, closing off the wrist. Without it the hand ends in a
    // flat cross-section that the near plane happily shows you the inside of.
    const cuff = new THREE.Mesh(
      new THREE.CylinderGeometry(0.048, 0.056, 0.09, 10),
      new THREE.MeshStandardMaterial({ color: 0x1d1a16, roughness: 1 }),
    );
    cuff.position.set(0.006, -0.012, 0.086);
    cuff.rotation.set(Math.PI / 2 - 0.22, 0, 0);
    group.add(cuff);

    build(group);
    return group;
  };
}

/**
 * A shaft running forward from the grip along −Z.
 *
 * `reach` is measured from the grip, so a wand's tip lands at WAND_TIP whatever
 * length of shaft it has. The grip sits inside the fist at the origin.
 */
function shaft(
  reach: number, near: number, far: number, colour: number, roughness = 0.8,
): THREE.Mesh {
  const length = reach + 0.05;
  const mesh = new THREE.Mesh(
    new THREE.CylinderGeometry(far, near, length, 8),
    new THREE.MeshStandardMaterial({ color: colour, roughness }),
  );
  mesh.rotation.x = Math.PI / 2;
  mesh.position.set(0, 0.012, 0.025 - length / 2);
  return mesh;
}

/**
 * The wand itself, without a hand around it.
 *
 * Separated from the viewmodel because the same four shapes are wanted in two
 * places: held in front of the camera, and attached to a mage's hand bone in
 * Blender for everyone else to see. A wand that exists twice is a wand that
 * stops matching itself the first time one copy is adjusted.
 *
 * Each is built around its **grip** at the origin, running forward along −Z to
 * WAND_TIP. That is what lets it be dropped onto a hand bone without anybody
 * having to work out where the handle is.
 */
export const WAND_BUILDERS: Record<ClassId, (g: THREE.Group) => void> = {
  // Gnarled wood and a raw crystal: the Magician is the elementalist, and their
  // wand is the one that looks grown rather than made.
  magician: (g) => {
    g.add(shaft(0.42, 0.013, 0.021, 0x4a3524, 1));
    const crystal = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.038, 0),
      new THREE.MeshStandardMaterial({
        color: 0x8fd4e8, roughness: 0.1, metalness: 0.2,
        emissive: 0x2a6a80, emissiveIntensity: 0.6,
      }),
    );
    crystal.position.set(WAND_TIP.x, WAND_TIP.y, WAND_TIP.z);
    g.add(crystal);
  },

  // Short, heavy and iron-bound. A Cleric is a close-range defender and their
  // wand is nearly a weapon, which is the same thing Wounding says.
  cleric: (g) => {
    g.add(shaft(0.36, 0.018, 0.023, 0x3a352c, 0.7));
    const head = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04, 0.04, 0.07, 6),
      new THREE.MeshStandardMaterial({ color: 0x6b5f4a, roughness: 0.45, metalness: 0.6 }),
    );
    head.rotation.x = Math.PI / 2;
    head.position.set(WAND_TIP.x, WAND_TIP.y, WAND_TIP.z + 0.02);
    g.add(head);
  },

  // Slender silver with a floating ring. Nothing about the Mentalist is heavy.
  mentalist: (g) => {
    g.add(shaft(0.44, 0.009, 0.012, 0xb9c2cc, 0.25));
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.036, 0.006, 6, 16),
      new THREE.MeshStandardMaterial({
        color: 0xdfe8f2, roughness: 0.2, metalness: 0.8,
        emissive: 0x3a5f8a, emissiveIntensity: 0.5,
      }),
    );
    ring.position.set(WAND_TIP.x, WAND_TIP.y, WAND_TIP.z);
    g.add(ring);
  },

  // An angular shard held in a clasp: the Arcanist works with the stuff itself.
  arcanist: (g) => {
    g.add(shaft(0.36, 0.014, 0.018, 0x241d2e, 0.6));
    const shard = new THREE.Mesh(
      new THREE.ConeGeometry(0.026, 0.12, 5),
      new THREE.MeshStandardMaterial({
        color: 0x2a1d3d, roughness: 0.35, metalness: 0.4,
        emissive: 0x6a3fb0, emissiveIntensity: 0.7,
      }),
    );
    shard.rotation.x = -Math.PI / 2;
    shard.position.set(WAND_TIP.x, WAND_TIP.y, WAND_TIP.z + 0.04);
    g.add(shard);
  },
};

/** The wand alone, no hand. What gets exported for Blender. */
export function buildWand(cls: ClassId): THREE.Group {
  const group = new THREE.Group();
  WAND_BUILDERS[cls](group);
  return group;
}

export const FALLBACKS: Record<AssetRole, Fallback> = {
  'mage.magician': mageFallback,
  'mage.cleric': mageFallback,
  'mage.mentalist': mageFallback,
  'mage.arcanist': mageFallback,

  'prop.torch': () =>
    standing(
      new THREE.Mesh(
        new THREE.SphereGeometry(0.16, 8, 8),
        new THREE.MeshBasicMaterial({ color: 0xffc46b }),
      ),
      0,
    ),

  'prop.shrine': (req) => {
    const colour = req.color ?? 0xffffff;
    return standing(
      new THREE.Mesh(
        new THREE.OctahedronGeometry(1.6, 0),
        new THREE.MeshStandardMaterial({
          color: colour, emissive: colour, emissiveIntensity: 1.4,
          roughness: 0.15, metalness: 0.4,
        }),
      ),
      0,
    );
  },

  'prop.pool': (req) => {
    const radius = req.size?.x ?? 4;
    const disc = new THREE.Mesh(
      new THREE.CircleGeometry(radius, 32),
      new THREE.MeshStandardMaterial({
        color: req.color ?? 0x6a6a6a, emissive: req.color ?? 0x6a6a6a,
        emissiveIntensity: 0.5, transparent: true, opacity: 0.55, roughness: 0.3,
      }),
    );
    disc.rotation.x = -Math.PI / 2;
    const group = new THREE.Group();
    group.add(disc);
    return group;
  },

  'viewmodel.magician': viewmodel(WAND_BUILDERS.magician),
  'viewmodel.cleric': viewmodel(WAND_BUILDERS.cleric),
  'viewmodel.mentalist': viewmodel(WAND_BUILDERS.mentalist),
  'viewmodel.arcanist': viewmodel(WAND_BUILDERS.arcanist),

  /*
   * No fallback of their own.
   *
   * A projectile shape with no model behind it falls back to the procedural
   * geometry in `vfx.ts`, which is where every shape is drawn today — so an
   * empty manifest is exactly the game as it stands, and a model is added by
   * naming one rather than by replacing anything.
   */
  'projectile.mace': () => new THREE.Group(),
  'projectile.blade': () => new THREE.Group(),
  'projectile.shard': () => new THREE.Group(),
  'projectile.stone': () => new THREE.Group(),
  'projectile.mass': () => new THREE.Group(),
  'projectile.barb': () => new THREE.Group(),
  'projectile.orb': () => new THREE.Group(),

  'level.bookshelf': (req) => {
    const size = req.size ?? { x: 0.6, y: 2, z: 1.5 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x4a3826, roughness: 0.95 }),
      ),
      size.y,
    );
  },

  'level.crate': (req) => {
    const size = req.size ?? { x: 1, y: 1, z: 1 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x6b5940, roughness: 0.92 }),
      ),
      size.y,
    );
  },

  /*
   * The furniture, gray-boxed.
   *
   * Every role needs one of these whether or not a model exists, because the
   * game has to run with no art at all — that is what made building it possible
   * before any of it was drawn, and it is what stops one missing file taking a
   * whole level with it. A block in a plausible colour is enough to stand on and
   * walk into, which is all a fallback owes anybody.
   */
  'prop.bench': (req) => {
    const size = req.size ?? { x: 1.6, y: 0.5, z: 0.6 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x6b5940, roughness: 0.9 }),
      ),
      size.y,
    );
  },

  'prop.cauldron': (req) => {
    const size = req.size ?? { x: 0.9, y: 0.8, z: 0.9 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x3a3a3f, roughness: 0.9 }),
      ),
      size.y,
    );
  },

  'prop.workbench': (req) => {
    const size = req.size ?? { x: 1.8, y: 0.9, z: 0.8 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x6b5940, roughness: 0.9 }),
      ),
      size.y,
    );
  },

  'prop.crateMetal': (req) => {
    const size = req.size ?? { x: 1, y: 1, z: 1 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x5a5f66, roughness: 0.9 }),
      ),
      size.y,
    );
  },

  'prop.crateFarm': (req) => {
    const size = req.size ?? { x: 1, y: 0.8, z: 1 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x7a6444, roughness: 0.9 }),
      ),
      size.y,
    );
  },

  'prop.arch': (req) => {
    const size = req.size ?? { x: 2, y: 3, z: 0.5 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x6f6558, roughness: 0.9 }),
      ),
      size.y,
    );
  },

  'prop.cage': (req) => {
    const size = req.size ?? { x: 1.2, y: 1.6, z: 1.2 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x4a4a50, roughness: 0.9 }),
      ),
      size.y,
    );
  },

  'prop.chain': (req) => {
    const size = req.size ?? { x: 0.8, y: 0.3, z: 0.8 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x4a4a50, roughness: 0.9 }),
      ),
      size.y,
    );
  },

  'prop.roofSupport': (req) => {
    const size = req.size ?? { x: 3, y: 0.4, z: 0.4 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x6b5940, roughness: 0.9 }),
      ),
      size.y,
    );
  },

  'level.pillar': (req) => {
    const size = req.size ?? { x: 1, y: 4, z: 1 };
    return standing(
      new THREE.Mesh(
        new THREE.BoxGeometry(size.x, size.y, size.z),
        new THREE.MeshStandardMaterial({ color: 0x3a3733, roughness: 0.9 }),
      ),
      size.y,
    );
  },
};
